import React, { useState, useRef, useEffect, forwardRef } from 'react';

interface LazyVideoProps extends React.VideoHTMLAttributes<HTMLVideoElement> {
  src: string;
  /** When true, load immediately (e.g. blob URLs). When false, use Intersection Observer. */
  eager?: boolean;
  /** 封面图：弱网或 metadata 未到时先显示，加快感知加载 */
  poster?: string;
}

/**
 * Video that only loads when near viewport.
 *
 * Placeholder strategy (no external poster images needed):
 *  - While src is not yet assigned → shimmer skeleton
 *  - Once src is assigned, browser fetches metadata (~KB) → first frame shows naturally
 *  - We track `loadedmetadata` (not `loadeddata`) so the shimmer disappears as soon
 *    as the first frame is ready, long before the full video downloads.
 */
const LazyVideo = forwardRef<HTMLVideoElement, LazyVideoProps>(function LazyVideo(
  { src, className, eager, poster, onLoadedData, ...rest },
  ref
) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [inView, setInView] = useState(false);
  const [metaReady, setMetaReady] = useState(false);

  const isBlobOrData = typeof src === 'string' && (src.startsWith('blob:') || src.startsWith('data:'));
  const shouldEager = eager === true || isBlobOrData;
  const effectiveSrc = (shouldEager || inView) ? src : undefined;

  useEffect(() => {
    if (shouldEager || inView) return;
    const el = containerRef.current;
    if (!el) return;
    const io = new IntersectionObserver(
      (entries) => {
        if (entries.some((e) => e.isIntersecting)) setInView(true);
      },
      { rootMargin: '200px', threshold: 0.01 }
    );
    io.observe(el);
    return () => io.disconnect();
  }, [shouldEager, inView]);

  // Reset metaReady if src changes (e.g. carousel switches video)
  useEffect(() => {
    setMetaReady(false);
  }, [src]);

  if (shouldEager) {
    return (
      <video
        ref={ref}
        src={src}
        poster={poster}
        className={className}
        onLoadedData={onLoadedData}
        preload="metadata"
        {...rest}
      />
    );
  }

  const fadeUntilMeta = !poster;

  return (
    <div ref={containerRef} className="relative w-full h-full min-h-0">
      {/* 无 poster 时用 shimmer；有 poster 时浏览器先显示 poster，不遮挡 */}
      {fadeUntilMeta && !metaReady && (
        <div
          aria-hidden
          className="absolute inset-0 bg-gradient-to-r from-gray-200 via-gray-100 to-gray-200 animate-shimmer bg-[length:200%_100%] rounded"
        />
      )}
      <video
        ref={ref}
        src={effectiveSrc}
        poster={poster}
        className={`w-full h-full min-h-0 ${className ?? ''} transition-opacity duration-150 ${
          fadeUntilMeta && !metaReady ? 'opacity-0' : 'opacity-100'
        }`}
        preload="metadata"
        onLoadedMetadata={() => setMetaReady(true)}
        onLoadedData={onLoadedData}
        {...rest}
      />
    </div>
  );
});

export default LazyVideo;
